import React, { useState } from 'react';
import { connect } from 'react-redux';
import { checkOutWithKhalti } from '../../helpers/payments';
const KHALTI_BY_IME_LOGO = 'https://khaltibyime.khalti.com/wp-content/uploads/2025/07/Logo-for-Blog.png';
import CardLogo from '../../images/card-logo.png';
import styles from '../../styles/payment.module.css';
import swal from 'sweetalert';

/**
 * PaymentMethodSelector - Smart payment method selector based on user's country
 * 
 * Shows:
 * - Khalti for Nepali users
 * - Card/HBL payment for Indian and foreign users
 * 
 * @param {Object} props
 * @param {Object} props.currentUser - Current user object from Redux
 * @param {Function} props.onPaymentInitiate - Callback when payment is initiated
 * @param {Object} props.paymentParams - Payment parameters (productIdentity, amount, productName, etc.)
 * @param {String} props.buttonLabel - Custom label for payment button (optional)
 * @param {Boolean} props.disabled - Disable payment buttons
 * @param {String} props.className - Additional CSS classes
 */
function PaymentMethodSelector({ 
  currentUser, 
  onPaymentInitiate,
  paymentParams,
  buttonLabel,
  disabled = false,
  className = ''
}) {
  const [processing, setProcessing] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);

  // Determine if user is from Nepal
  const isNepaliUser = () => {
    if (!currentUser || !currentUser.country) {
      // If no country info, default to showing both options or ask user
      return null; // Unknown - show both options
    }
    
    // Backend returns country as country_char (e.g., "NP" for Nepal) or country name
    const countryValue = currentUser.country;
    
    // Check if it's Nepal by country code or name
    if (typeof countryValue === 'string') {
      const countryUpper = countryValue.toUpperCase();
      // Nepal country codes: NP, NPL
      if (countryUpper === 'NP' || countryUpper === 'NPL') {
        return true;
      }
      // Check by name
      if (countryValue.toLowerCase() === 'nepal') {
        return true;
      }
    }
    
    // If it's an object with name property
    if (countryValue && countryValue.name) {
      return countryValue.name.toLowerCase() === 'nepal';
    }
    
    // If country_char is NP
    if (countryValue && countryValue.country_char) {
      return countryValue.country_char.toUpperCase() === 'NP';
    }
    
    // Default: show both options if we can't determine
    return null;
  };

  const userIsNepali = isNepaliUser();

  // Handle Khalti payment
  const handleKhaltiPayment = async () => {
    if (disabled || processing) return;

    // Remove this guard once Stripe is fully integrated.
    const currency = paymentParams?.currencyCode;
    if (currency === 'USD') {
      swal('Payment method not available', 'USD payments require card (Stripe). Khalti supports NPR only.', 'info');
      return;
    }

    setProcessing(true);
    setSelectedMethod('khalti');
    
    try {
      await checkOutWithKhalti({
        productIdentity: paymentParams.productIdentity,
        amount: paymentParams.amount,
        productName: paymentParams.productName || 'Visit All Nepal Booking',
        productUrl: paymentParams.productUrl,
        customerName: paymentParams.customerName,
        customerEmail: paymentParams.customerEmail,
        customerPhone: paymentParams.customerPhone
      });
      
      if (onPaymentInitiate) {
        onPaymentInitiate('khalti');
      }
    } catch (error) {
      console.error('Khalti payment error:', error);
      swal('Payment Error', 'Could not initiate Khalti payment. Please try again.', 'error');
    } finally {
      setProcessing(false);
      setSelectedMethod(null);
    }
  };

  // Handle Card/HBL payment (for non-Nepali users)
  const handleCardPayment = () => {
    if (disabled || processing) return;

    // Remove this guard once Stripe is fully integrated.
    const currency = paymentParams?.currencyCode;
    if (currency === 'NPR') {
      swal('Payment method not available', 'NPR payments are currently processed via Khalti.', 'info');
      return;
    }
    
    // TODO: Implement HBL card payment flow
    // For now, show a message that this will be implemented
    swal(
      'Card Payment',
      'Card payment option will be available soon. For now, please contact us for alternative payment methods.',
      'info'
    );
    
    // When implemented, this should:
    // 1. Call the HBL payment API
    // 2. Show payment form or redirect to payment gateway
    // 3. Handle callback
  };

  // If user country is unknown, show both options
  if (userIsNepali === null) {
    return (
      <div className={`${styles.paymentActions} ${className}`}>
        <h5 className="font-weight-bold text-center">CONTINUE TO PAYMENT</h5>
        <div className={styles.paymentBody}>
          <div
            className={`${styles.action} ${processing && selectedMethod === 'khalti' ? 'disabled' : ''}`}
            onClick={handleKhaltiPayment}
            style={{ 
              cursor: (disabled || processing) ? 'not-allowed' : 'pointer',
              opacity: (disabled || processing) ? 0.6 : 1 
            }}
          >
            <img src={KHALTI_BY_IME_LOGO} className="logo" alt="Khalti by IME" />
            <div className={`${styles.label} text-primary`}>
              {processing && selectedMethod === 'khalti' ? 'Processing...' : 'Pay with Khalti'}
            </div>
            <small className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
              For Nepali users
            </small>
          </div>

          <div
            className={`${styles.action} ${processing && selectedMethod === 'card' ? 'disabled' : ''}`}
            onClick={handleCardPayment}
            style={{ 
              cursor: (disabled || processing) ? 'not-allowed' : 'pointer',
              opacity: (disabled || processing) ? 0.6 : 1 
            }}
          >
            <img src={CardLogo} className="logo" style={{ width: '65px' }} alt="Card Payment" />
            <div className={`${styles.label} text-primary`}>
              {processing && selectedMethod === 'card' ? 'Processing...' : 'Pay with Card'}
            </div>
            <small className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
              For international users
            </small>
          </div>
        </div>
      </div>
    );
  }

  // Show appropriate payment method based on user's country
  return (
    <div className={`${styles.paymentActions} ${className}`}>
      <h5 className="font-weight-bold text-center">
        {buttonLabel || 'CONTINUE TO PAYMENT'}
      </h5>
      <div className={styles.paymentBody}>
        {userIsNepali ? (
          // Nepali user - Show Khalti
          <div
            className={`${styles.action} ${processing ? 'disabled' : ''}`}
            onClick={handleKhaltiPayment}
            style={{ 
              cursor: (disabled || processing) ? 'not-allowed' : 'pointer',
              opacity: (disabled || processing) ? 0.6 : 1 
            }}
          >
            <img src={KHALTI_BY_IME_LOGO} className="logo" alt="Khalti by IME" />
            <div className={`${styles.label} text-primary`}>
              {processing ? 'Processing...' : (buttonLabel || 'Pay with Khalti')}
            </div>
          </div>
        ) : (
          // Non-Nepali user (Indian/Foreign) - Show Card payment
          <div
            className={`${styles.action} ${processing ? 'disabled' : ''}`}
            onClick={handleCardPayment}
            style={{ 
              cursor: (disabled || processing) ? 'not-allowed' : 'pointer',
              opacity: (disabled || processing) ? 0.6 : 1 
            }}
          >
            <img src={CardLogo} className="logo" style={{ width: '65px' }} alt="Card Payment" />
            <div className={`${styles.label} text-primary`}>
              {processing ? 'Processing...' : (buttonLabel || 'Pay with Card')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  currentUser: state.userStore?.currentUser || {}
});

export default connect(mapStateToProps)(PaymentMethodSelector);
